import { PanelSettings } from "@/types";
import { TabContent } from "./components";
import {
  DEFAULT_FILTER_KEY,
  DEFAULT_FOLDER_KEY,
  FILTERS_TAB_KEY,
  FOLDERS_TAB_KEY,
  defaultSettings,
} from "@/constants";
import {
  CacheService,
  FiltersService,
  FoldersService,
  TreeService,
} from "@/services";
import { IPanelScope } from "@/interfaces";
import { LocalStorageKeys } from "@/enums";
import { FilterListMessage, Panel, Tabs } from "@/shared-components";
import { FilterProvider, FoldersProvider } from "@/contexts";
import { FilterList } from "./components";
import { Loading } from "@/shared-components";
import { useRef, useState } from "react";
import { MuxTabFilter, MuxTabFolder } from "@/Icons/PtIcons";
import { FolderList } from "./components/FoldersPanel";
import styles from "./FiltersFoldersTabsPanel.module.scss";
import { getTranslation } from "@/utils";

export interface FiltersProps {
  scope: IPanelScope;
  isDataReady: boolean;
  filters: any[];
  title: string;
  settings: PanelSettings;
  strings: Record<string, string>;
  newFilterId?: string;
  notifyFilterChanged: (_args: any, isUserAction: boolean) => void;
  notifyFolderChanged?: (_args: any) => void;
  getCljsIcon: (icon: string, props: any) => any;
  editView?: (id: string) => void;
  createView?: (
    parentId: string | null,
    filterType?: "manual" | "auto",
  ) => void;
  copyAsPtql?: (filterId: string, autoFilterValues?: string[]) => void;
  moveView?: (id: string) => void;
}

export const FiltersPanel = ({
  title,
  scope,
  isDataReady,
  settings = defaultSettings,
  newFilterId,
  notifyFilterChanged = () => {},
  notifyFolderChanged = () => {},
  editView,
  createView,
  copyAsPtql,
  moveView,
}: FiltersProps) => {
  const { module, projectId, userId } = scope;
  const [activeTab, setActiveTab] = useState<string>(
    TreeService.getSelectedTab(scope),
  );
  const panelSettings = { defaultSettings, ...settings };
  const [panelScope, setPanelScope] = useState<IPanelScope>({
    ...scope,
    tab: TreeService.getSelectedTab(scope),
  });
  // without bookmarks (e.g. in a modal) keep each tab's url here, so switching back restores its selection
  const tabUrls = useRef<Record<string, string>>({});
  const filtersText = getTranslation("filters-tree.tab.filters", "Filters");
  const foldersText = getTranslation("filters-tree.tab.folders", "Folders");
  CacheService.clearFilterCacheIfNeeded(panelScope, settings.cacheVersion);

  if (!window.location.search && !settings.disableBookmarks) {
    const lastUrl = TreeService.getLastUrl({
      ...scope,
      tab: TreeService.getSelectedTab(scope),
    });

    window.history.pushState({}, "", lastUrl);
  }

  if (!userId || !module || !projectId) {
    console.error(`FiltersPanel->init scope is missing!`, {
      userId,
      module,
      projectId,
    });
    return <FilterListMessage message="Opps something went wrong!" />;
  }
  const handleFilterChange = (node: any, isUserAction: boolean = true) => {
    if (!node?.id) {
      node = { id: DEFAULT_FILTER_KEY };
    }

    const gridfilter = isUserAction ? {} : FiltersService.getGridFilters();
    const objectToServer = node?.serverObject || { id: DEFAULT_FILTER_KEY };
    notifyFilterChanged({ ...objectToServer, gridfilter }, isUserAction);

    const lastUrl = FiltersService.pushToHistory(node, isUserAction);
    if (!settings.disableBookmarks) {
      TreeService.setLastUrl(panelScope, lastUrl);
    }
  };

  const handleFolderChange = (node: any) => {
    if (!node?.id) {
      node = { id: DEFAULT_FOLDER_KEY };
    }

    const lastUrl = FoldersService.pushToHistory(node);
    if (!settings.disableBookmarks) {
      TreeService.setLastUrl(panelScope, lastUrl);
    }
    notifyFolderChanged(node);
  };

  const filtersProvider = (
    <FilterProvider
      scope={panelScope}
      settings={panelSettings}
      newFilterId={newFilterId}
      notifyFilterChanged={handleFilterChange}
      editView={editView}
      createView={createView}
      copyAsPtql={copyAsPtql}
      moveView={moveView}
    >
      <TabContent>
        <FilterList
          notifyFilterChanged={handleFilterChange}
          maxNumberOfFavorites={settings.maxNumberOfFavorites}
        />
      </TabContent>
    </FilterProvider>
  );

  const onTabChange = (tab: string) => {
    const newScope = { ...scope, tab };
    let lastUrl = TreeService.getLastUrl(newScope);
    if (settings.disableBookmarks) {
      tabUrls.current[activeTab] = window.location.search;
      lastUrl = tabUrls.current[tab] ?? lastUrl;
    }

    CacheService.setItemToPersistCache(
      panelScope,
      LocalStorageKeys.SELECTED_TAB_KEY,
      tab,
    );

    setActiveTab(tab);
    setPanelScope(newScope);

    window.history.pushState({}, "", lastUrl);
  };

  const tabs = [
    {
      id: FILTERS_TAB_KEY,
      label: filtersText,
      content: filtersProvider,
      onTabChange,
      Icon: MuxTabFilter,
    },
  ];
  if (settings?.enableFolders) {
    tabs.push({
      id: FOLDERS_TAB_KEY,
      label: foldersText,
      onTabChange,
      content: (
        <FoldersProvider scope={panelScope} settings={panelSettings}>
          <TabContent>
            <FolderList
              notifyFolderChange={handleFolderChange}
              maxNumberOfFavorites={settings.maxNumberOfFavorites}
            />
          </TabContent>
        </FoldersProvider>
      ),
      Icon: MuxTabFolder,
    });
  }

  return (
    <Panel
      title={`${title || filtersText}`}
      enableResize={panelSettings.enableResize}
    >
      {!isDataReady ? (
        <div className={styles.loaderContainer}>
          <Loading size="modal" />
        </div>
      ) : tabs.length > 1 ? (
        <Tabs
          defaultActiveTabId={activeTab}
          tabClassName={styles.tab}
          tabsListClassName={styles["tabs-list"]}
          tabContentClassName={styles["tab-content"]}
          tabs={tabs}
        />
      ) : (
        <>
          <div className={styles.title}>
            <span className={styles.icon}>
              <MuxTabFilter />
            </span>
            {filtersText}
          </div>
          <div className={styles["tab-content"]}>{filtersProvider}</div>
        </>
      )}
    </Panel>
  );
};
